import {StipTypes, ExtraData} from '@deliveriesondemand/dod-data';
import {Component} from 'react';
import {connect} from 'react-redux';
import {formatNumber} from 'accounting';
import injectSheet from 'react-jss';

import {getStipIcon} from '-/icons/icons';
import StipsService from '-/services/StipsService';
import {findImageData, applicantNameSelector} from '-/selectors';
import {addDoc, removeDoc} from '-/api/api';
import {getOfferId, getVinVehicleInfo} from '-/util/util';
import ToastService from '-/services/ToastService';
import {renderError} from '-/util/error';
import NavigationService from '-/services/NavigationService';
import Fieldset from '-/components/fields/Fieldset';
import {important} from '-/styles/utils';
import {get} from '-/util/lodash';

const stipTypeHasExtra = type => {
    return ['CHKLOC', 'EXM', 'MMN', 'PYMT', 'REF', 'VIN'].includes(type);
};

const styles = theme => ({
    imgContainer: {
        marginBottom: important(10),
        margin: '0 auto',
        width: 200,
        height: 150,
        border: '1px solid gray',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 15,
        position: 'relative',
        '& img': {
            maxWidth: '100%',
            maxHeight: '100%'
        }
    },
    buttonContainer: {
        marginTop: 10
    },
    submitted: {
        position: 'absolute',
        color: 'darkgreen'
    },
    mileage: {
        position: 'absolute',
        bottom: 15
    },
    link: {
        color: theme.colors.primary,
        textDecoration: 'underline'
    }
});

class Stip extends Component {
    constructor(props) {
        super(props);
        this.state = {
            removing: false,
            adding: false,
            canChange: true,
            canRemove: true
        };
    }

    errorHandler = error => {
        const errorType = get(error, 'response.data.error.errorType');

        if (errorType === 'fileUploadStatusBlock') {
            this.setState({canChange: false});
        }
    };

    openDialog = () => {
        const {stip, doc} = this.props;
        const {canChange} = this.state;

        if (!canChange) {
            return false;
        }

        if (stipTypeHasExtra(stip.docType)) {
            NavigationService.next('stipEdit', {stip, doc});
        } else {
            StipsService.openDialog({
                stip,
                doc,
                errorHandler: this.errorHandler
            });
        }
    };

    getParams() {
        const {
            offerId,
            stip,
            applicantType
        } = this.props;
        return {
            offerId,
            stipType: stip.docType,
            applicantType
        };
    }

    removeDoc = () => {
        const {stip, indexOfDoc} = this.props;

        this.setState({removing: true});

        const removeParams = {
            ...this.getParams(),
            indexOfDoc
        };

        removeDoc(removeParams).then(() => {
            this.setState({removing: false});
            StipsService.removeDoc(stip, indexOfDoc);
        }).catch(err => {
            this.setState({removing: false});

            const errorType = get(err, 'response.data.error.errorType');

            if (errorType === 'fileRemovalMissing') {
                // if file has already been removed by Admin, just remove it from interface
                StipsService.removeDoc(stip, indexOfDoc);
            } else if (errorType === 'fileRemovalStatus') {
                this.setState({canChange: false, canRemove: false});
            }

            ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
        });
    };

    addDoc = () => {
        this.setState({adding: true});
        addDoc(this.getParams()).then(res => {
            const {doc} = res.data;
            const {stip} = this.props;
            this.setState({adding: false});
            StipsService.addDoc(stip, doc);
            StipsService.openDialog({
                stip,
                doc
            });
        }).catch(err => {
            this.setState({adding: false});
            ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
        });
    };

    renderUploadButton() {
        const {canChange} = this.state;
        const {
            stip: {
                docType
            },
            doc,
            imageData,
            classes = {}
        } = this.props;

        if (!canChange) {
            return null;
        }

        const extraFilterFn = key => {
            if (key === 'mileage') {
                return get(doc, 'extra.mileage', 0) <= 0;
            } else if (key === 'checkPickupLocation') {
                return !get(doc, 'extra.checkPickup.address.name');
            } else {
                return !doc.extra[key];
            }
        };

        let changeText = 'Upload';
        if (imageData) {
            changeText = 'Change';
        }

        if (stipTypeHasExtra(docType)) {
            const refFields = Object.keys(ExtraData[docType].fields);
            const nEmpty = refFields.filter(extraFilterFn).length;
            changeText = `Enter ${StipTypes[docType]}`;

            if (nEmpty < refFields.length) {
                changeText = 'Change';
            }
        }

        return (
            <a className={classes.link} onClick={this.openDialog}>
                {changeText}
            </a>
        );
    }

    render() {
        const {
            stip,
            doc,
            imageData,
            indexOfDoc,
            classes = {},
            applicantName,
            vehicleDesc,
            coAppUnlocked
        } = this.props;
        const {canRemove, removing, adding} = this.state;

        const {docType} = stip;
        let showAddButton = ['POI', 'OLD', 'STIPS'].includes(docType);
        if (showAddButton) {
            showAddButton = !!imageData && indexOfDoc === stip.docs.length - 1;
        }

        let additionalTitle;
        if (['DL1', 'DL2', 'POI'].includes(docType) && coAppUnlocked) {
            additionalTitle = `For ${applicantName}`;
        } else if (docType.match('INS')) {
            additionalTitle = vehicleDesc;
        }

        let titleStyle;
        if (!doc.subtitle && additionalTitle) {
            titleStyle = {marginBottom: 0};
        }

        let subtitleStyle;
        if (additionalTitle) {
            subtitleStyle = {marginBottom: 0};
        }

        return (
            <Fieldset>
                <p
                    className="info"
                    onClick={this.openDialog}
                    style={titleStyle}>
                    {doc.title}
                </p>
                {doc.subtitle && (
                    <p className="info" style={subtitleStyle}>{doc.subtitle}</p>
                )}
                {additionalTitle && (
                    <p className="smallInfo">{additionalTitle}</p>
                )}
                <div className={classes.imgContainer}>
                    {stip.docType === 'EXM' && (
                        <p className={classes.mileage}>{formatNumber(get(doc, 'extra.mileage'))}</p>
                    )}
                    {stipTypeHasExtra(stip.docType) && doc.submitted && (
                        <i
                            className={`${classes.submitted} fa fa-check fa-5x`}
                            onClick={this.openDialog} />
                    )}
                    <img src={imageData || getStipIcon(stip.docType)} onClick={this.openDialog} />
                </div>
                {doc.description && <p>{doc.description}</p>}
                {doc.message && (
                    <p style={{color: 'red'}}>({doc.message})</p>
                )}
                <div className={classes.buttonContainer}>
                    {!(doc.isDefault || indexOfDoc < stip.docs.length-1) && doc.status !== 'REQUIRED' && canRemove && (
                        <span>
                            {removing ? (
                                <span>Removing</span>
                            ) : (
                                <a onClick={this.removeDoc}>Remove</a>
                            )}
                        </span>
                    )}
                    &nbsp;
                    {this.renderUploadButton()}
                    &nbsp;
                    {showAddButton && (
                        <span>
                            {adding ? (
                                <span>Adding</span>
                            ) : (
                                <a onClick={this.addDoc}>
                                    <i className="fa fa-plus-circle" />
                                    More Photos
                                </a>
                            )}
                        </span>
                    )}
                </div>
            </Fieldset>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const indexOfDoc = ownProps.stip.docs.findIndex(doc => doc._id === ownProps.doc._id);
    const offerId = getOfferId();
    const applicantName = applicantNameSelector(state);
    const {vehicleInfo = {}} = state;
    const vehicleDesc = getVinVehicleInfo(vehicleInfo);

    return {
        imageData: findImageData(state, ownProps.stip.docType, indexOfDoc),
        coAppUnlocked: state.navigation.coAppUnlocked,
        applicantType: state.stips.applicantType,
        applicantName,
        vehicleDesc,
        indexOfDoc,
        offerId
    };
};

export default connect(mapStateToProps)(injectSheet(styles)(Stip));
